import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Coupon, CreateCouponData } from './api/coupons.service';
import { DashboardLayout } from '@/layout/DashboardLayout';
import { ConfirmDialog } from '@/design-system';
import { useErrorToast } from '@/core/errors/useErrorToast';
import { useCouponsListQuery } from './hooks/useCouponsListQuery';
import { useCouponMutations } from './hooks/useCouponMutations';
import { CouponFormDialog } from './components/CouponFormDialog';
import { CouponTable, type CouponTableHandlers } from './components/CouponTable';
import { couponToFormData, emptyCouponForm } from './utils/coupon-form';

export function CouponsPage() {
  const { data: coupons = [], isLoading, isError, error } = useCouponsListQuery();
  const onErrorToast = useErrorToast();
  const { saveCoupon, deactivateCoupon, deleteCoupon } = useCouponMutations();

  useEffect(() => {
    if (isError && error) onErrorToast(error, 'Erro ao carregar cupons');
  }, [isError, error, onErrorToast]);

  const [showCouponFormDialog, setShowCouponFormDialog] = useState(false);
  const [couponFormMode, setCouponFormMode] = useState<'create' | 'edit'>('create');
  const [editingCouponId, setEditingCouponId] = useState<string | null>(null);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [formData, setFormData] = useState<CreateCouponData>(emptyCouponForm());
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const resetFormDialog = useCallback(() => {
    setShowCouponFormDialog(false);
    setEditingCouponId(null);
    setFormData(emptyCouponForm());
    setFormErrors({});
  }, []);

  const validateForm = () => {
    const errors: Record<string, string> = {};
    const code = formData.code.trim().toUpperCase();
    if (!code) errors.code = 'Código é obrigatório';
    else if (code.length < 2) errors.code = 'Código deve ter no mínimo 2 caracteres';
    if (formData.discountStrategy === 'PERCENTAGE' && (formData.value < 0 || formData.value > 100))
      errors.value = 'Desconto deve ser entre 0 e 100%';
    if (formData.discountStrategy === 'FIXED_AMOUNT' && formData.value < 0)
      errors.value = 'Valor deve ser maior ou igual a zero';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveCoupon = async () => {
    if (!validateForm()) return;
    try {
      await saveCoupon.mutateAsync({
        mode: couponFormMode,
        editingId: editingCouponId,
        formData,
      });
      resetFormDialog();
    } catch {
      /* toast in mutation */
    }
  };

  const tableHandlers = useMemo<CouponTableHandlers>(
    () => ({
      onCreate: () => {
        setCouponFormMode('create');
        setEditingCouponId(null);
        setFormData(emptyCouponForm());
        setFormErrors({});
        setShowCouponFormDialog(true);
      },
      onEdit: (c) => {
        setCouponFormMode('edit');
        setEditingCouponId(c.id);
        setFormData(couponToFormData(c));
        setFormErrors({});
        setShowCouponFormDialog(true);
      },
      onDeactivate: (c) => {
        setSelectedCoupon(c);
        setShowDeactivateDialog(true);
      },
      onDelete: (c) => {
        setSelectedCoupon(c);
        setShowDeleteDialog(true);
      },
    }),
    []
  );

  return (
    <DashboardLayout title="Cupons">
      <div className="space-y-4">
        <CouponTable coupons={coupons} loading={isLoading} handlers={tableHandlers} />
      </div>

      <CouponFormDialog
        isOpen={showCouponFormDialog}
        mode={couponFormMode}
        formData={formData}
        formErrors={formErrors}
        isSubmitting={saveCoupon.isPending}
        onClose={resetFormDialog}
        onChange={setFormData}
        onSave={() => void handleSaveCoupon()}
      />

      <ConfirmDialog
        isOpen={showDeactivateDialog}
        onClose={() => {
          setShowDeactivateDialog(false);
          setSelectedCoupon(null);
        }}
        onConfirm={() => {
          if (!selectedCoupon) return;
          void deactivateCoupon.mutateAsync(selectedCoupon.id).then(() => {
            setShowDeactivateDialog(false);
            setSelectedCoupon(null);
          });
        }}
        title="Desativar cupom"
        message={`Desativar o cupom "${selectedCoupon?.code}"? Ele não poderá mais ser utilizado no checkout.`}
        confirmText="Desativar"
        cancelText="Cancelar"
        variant="danger"
        isLoading={deactivateCoupon.isPending}
      />
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setSelectedCoupon(null);
        }}
        onConfirm={() => {
          if (!selectedCoupon) return;
          void deleteCoupon.mutateAsync(selectedCoupon.id).then(() => {
            setShowDeleteDialog(false);
            setSelectedCoupon(null);
          });
        }}
        title="Excluir cupom"
        message={`Excluir permanentemente o cupom "${selectedCoupon?.code}"? Só é permitido se nunca foi resgatado.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        variant="danger"
        isLoading={deleteCoupon.isPending}
      />
    </DashboardLayout>
  );
}
